import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";

const Attachment = ({ isPdfonly }: { isPdfonly: boolean }) => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { errors, setErrors, setAttachment } = usePortServicesFlow();

  const attachmentUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const attachment = useUploadStore((s) => s.filesByKey.attachment ?? EMPTY_UPLOAD_ENTRIES);

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = isPdfonly
    ? fileProfiles.portServicesRequestFile
    : fileProfiles.rejectPortServiceDrawer;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("attachment");
        return;
      }
      setFilesForKey("attachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeAttachment = () => {
    clearFilesForKey("attachment");
    setAttachment({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setAttachment({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: file?.type,
          });
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: { attachment?: string; files?: string }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ attachment: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors({ files: undefined });
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      attachmentUploadSignatureRef.current = null;
      removeAttachment();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-attachment">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.attachment`)}
      </Typography>
      <DocumentUpload
        testId="port-services-attachment-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setAttachmentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "ATTACHMENT_DOCUMENT"),
            uploadTracker: attachmentUploadSignatureRef,
          })
        }
        name="attachment"
        conditions={internalProfile}
        files={attachment}
        setFiles={(next) => setFilesForKey("attachment", next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={
          isPdfonly ? t("upload.pdf-only") : t("upload.drag-or-drop-description-10mb")
        }
      />
      {errors.attachment && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.attachment}</FormHelperText>
      )}
      {attachment[0] && (
        <SingleFile
          file={attachment[0]}
          testId="port-services-attachment"
          fieldName="attachment"
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, files: undefined });
            setAttachmentFiles([]);
            attachmentUploadSignatureRef.current = null;
          }}
          removeFn={removeAttachment}
        />
      )}
    </div>
  );
};

export default Attachment;
